/** @jsx jsx */
import React from "react"
import { graphql } from "gatsby"
import { jsx, Box, Container, Heading, Flex } from "theme-ui"
import { BgImage } from "gbimage-bridge"
import SideBySideBlock from "./SideBySideBlock"
import Action from "../../shared/Action"
import "./sideBySide.css"

export const query = graphql`
  fragment ContentfulSideBySideGroupSectionFragment on ContentfulSideBySideGroupSection {
    backgroundImage {
      gatsbyImageData
    }
    backgroundColor
    backgroundStyle
    spaceBetweenBlocks
    isDark
    h3Heading
    button {
      classnameForAnalytics
      actionEvent
      fullWidth
      icon
      iconSize
      id
      style
      label
      url
      form {
        ...FormFragment
      }
      hubspotForm {
        formId
        portalId
        region
      }
    }
    sideBySideBlocks {
      id
      content {
        raw
        references {
          ...ActionFragment
        }
      }
      imageBackgroundColor
      imagePosition
      desktopImage {
        description
        file {
          contentType
          url
        }
        gatsbyImageData
      }
      mobileImage {
        description
        file {
          contentType
          url
        }
        gatsbyImageData
      }
      textContainerWidth
      classname
      wistiaItem {
        wistiaHeading
        maxVideosWidth
        videosAlignment
        videos {
          items {
            hashed_id
          }
        }
      }
    }
  }
`

const SideBySideGroupSection = ({ sectionContent }) => {
  const {
    backgroundColor,
    h3Heading,
    button,
    sideBySideBlocks,
    backgroundImage,
    isDark,
    backgroundStyle,
    spaceBetweenBlocks,
  } = sectionContent

  const h3HeadingColor = isDark ? "#ffffff" : "#404041"

  let position

  switch (backgroundStyle) {
    case "Right Bottom":
      position = "bottom right !important"
      break
    case "Right Top":
      position = "top right !important"
      break
    case "Left Bottom":
      position = "bottom left !important"
      break
    case "Left Top":
      position = "top left !important"
      break
    default:
      position = "center"
  }

  let backgroundImgStyles = {}

  if (backgroundStyle !== "Cover" && backgroundStyle !== null) {
    backgroundImgStyles = {
      "::before": {
        backgroundPosition: position,
        backgroundSize: "auto !important",
      },
      "::after": {
        backgroundPosition: position,
        backgroundSize: "auto !important",
      },
    }
  }

  const gapNumber = parseInt(spaceBetweenBlocks)

  return (
    <Box sx={{ bg: backgroundColor, position: "relative", pt: ["16px", "16px"] }}>
      <BgImage image={backgroundImage?.gatsbyImageData} sx={{ marginInline: "auto", ...backgroundImgStyles }}>
        <Container variant="containerSecondary">
          <Box
            sx={{
              textAlign: "center",
              margin: ["0 auto 0", "0 auto 30px"],
            }}
          >
            {h3Heading && (
              <Heading as="h3" variant="bold" pt="80px" pb="10px" color={h3HeadingColor}>
                {h3Heading}
              </Heading>
            )}
          </Box>
          <Flex
            sx={{
              flexDirection: "column",
              gap: [`${gapNumber / 2}px`, `${gapNumber / 2}px`, `${gapNumber}px`],
            }}
          >
            {sideBySideBlocks.map((block, index) => (
              <Box
                key={block.id}
                sx={{
                  paddingBottom: index === sideBySideBlocks.length - 1 && "50px",
                }}
              >
                <SideBySideBlock block={block} isDark={isDark} />
              </Box>
            ))}
          </Flex>
          <Flex sx={{ justifyContent: "center" }}>
            {button && (
              <Flex
                sx={{
                  width: "100%",
                  justifyContent: ["center", "flex-start", null, "center"],
                  alignItems: "center",
                  marginBottom: "50px",
                }}
              >
                <Box mt={2} sx={{ marginLeft: ["0", "20px", "40px", "0"] }}>
                  <Action
                    {...button}
                    wordWrap={true}
                    fontsize={["12px", "18px"]}
                    classnameForAnalytics={button.classnameForAnalytics}
                  />
                </Box>
              </Flex>
            )}
          </Flex>
        </Container>
      </BgImage>
    </Box>
  )
}

export default SideBySideGroupSection
